import { useEffect, useState } from "react";
import { CreditCard, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export type DonationCryptoSymbol = "USDT" | "USDC";
export type DonationCryptoChain = "BSC_TESTNET" | "SOLANA_DEVNET";

export type DonationCryptoDetails = {
  crypto: DonationCryptoSymbol;
  chain: DonationCryptoChain;
  baseFiat: string;
};

export type DonationPayerDetails = {
  email: string;
  phone: string;
  fullName: string;
};

interface DonationPaymentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  amount: number;
  charityLabel: string;
  payerDetails: DonationPayerDetails;
  onPayerDetailsChange: (field: keyof DonationPayerDetails, value: string) => void;
  cryptoDetails: DonationCryptoDetails;
  onCryptoDetailsChange: (field: keyof DonationCryptoDetails, value: string) => void;
  isCryptoTestMode: boolean;
  isProcessing: boolean;
}

export default function DonationPaymentDetailsModal({
  isOpen,
  onClose,
  onProceed,
  amount,
  charityLabel,
  payerDetails,
  onPayerDetailsChange,
  cryptoDetails,
  onCryptoDetailsChange,
  isCryptoTestMode,
  isProcessing,
}: DonationPaymentDetailsModalProps) {
  const { t } = useTranslation();
  const [displayAmount, setDisplayAmount] = useState("");
  const [validationError, setValidationError] = useState("");

  const handleProceed = () => {
    setValidationError("");

    if (!payerDetails.email.trim()) {
      return setValidationError("Email Address is required.");
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payerDetails.email.trim())) {
      return setValidationError("Please enter a valid email address.");
    }
    if (payerDetails.phone.trim().length < 5) {
      return setValidationError("Phone number must be at least 5 characters long.");
    }
    if (payerDetails.fullName.trim().length < 2) {
      return setValidationError("Full name must be at least 2 characters long.");
    }
    
    if (!cryptoDetails.crypto) return setValidationError("Please select a crypto currency.");
    if (!cryptoDetails.chain) return setValidationError("Please select a network chain.");
    
    const numAmount = Number(displayAmount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return setValidationError("Amount must be a valid number greater than 0.");
    }

    onProceed();
  };

  useEffect(() => {
    if (!isOpen) return;
    setDisplayAmount(Number.isFinite(amount) ? amount.toFixed(2) : "");
  }, [amount, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#2a2b2e] w-[92vw] max-w-[600px] max-h-[85vh] rounded-2xl shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-[#2a2b2e] z-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
            {t("enduser.plan.payment.completeTitle", { defaultValue: "Complete Payment" })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        {validationError && (
          <div className="px-4 sm:px-6 py-3 bg-red-50 dark:bg-red-900/20 border-b border-red-100 dark:border-red-800/30">
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              {validationError}
            </p>
          </div>
        )}

        <div className="px-4 sm:px-6 py-4 bg-blue-50 dark:bg-blue-900/20 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {t("enduser.donate.selectNgo", { defaultValue: "Charity" })}
              </p>
              <p className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {charityLabel}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {t("enduser.plan.payment.amountLabel", { defaultValue: "Amount" })}
              </p>
              <p className="text-xl sm:text-2xl font-bold text-blue-600">
                ${displayAmount}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("enduser.plan.payment.emailLabel", { defaultValue: "Email Address" })}
            </label>
            <input
              type="email"
              placeholder="your@email.com"
              value={payerDetails.email}
              onChange={(e) => onPayerDetailsChange("email", e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("enduser.plan.payment.phoneLabel", { defaultValue: "Phone Number" })}
            </label>
            <input
              type="tel"
              placeholder="+234 xxx xxx xxxx"
              value={payerDetails.phone}
              onChange={(e) => onPayerDetailsChange("phone", e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("enduser.plan.payment.fullNameLabel", { defaultValue: "Full Name" })}
            </label>
            <input
              type="text"
              placeholder="John Doe"
              value={payerDetails.fullName}
              onChange={(e) => onPayerDetailsChange("fullName", e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 sm:p-5 bg-gray-50 dark:bg-[#252628] space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Crypto
                </label>
                <select
                  value={cryptoDetails.crypto}
                  onChange={(e) => onCryptoDetailsChange("crypto", e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="USDT">USDT</option>
                  <option value="USDC">USDC</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Chain
                </label>
                <select
                  value={cryptoDetails.chain}
                  onChange={(e) => onCryptoDetailsChange("chain", e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="BSC_TESTNET">BSC Testnet</option>
                  <option value="SOLANA_DEVNET">SOLANA DEVNET</option>
                  {/* <option value="POLYGON">Polygon</option> */}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Amount
              </label>
              <input
                type="text"
                value={displayAmount}
                onChange={(e) => setDisplayAmount(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {isCryptoTestMode && (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Test mode enabled. Chain will be sent as BSC_TESTNET.
              </p>
            )}
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CreditCard className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {t("enduser.plan.payment.secureCryptoTitle", { defaultValue: "Secure Payment via Crypto" })}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {t("enduser.plan.payment.secureCryptoDesc", { defaultValue: "We support USDT and USDC across multiple networks" })}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 sticky bottom-0 bg-white dark:bg-[#2a2b2e]">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-6 py-2.5 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors order-2 sm:order-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("enduser.plan.actions.cancel", { defaultValue: "Cancel" })}
          </button>
          <button
            onClick={handleProceed}
            disabled={isProcessing}
            className="px-8 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 order-1 sm:order-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="text-sm sm:text-base">
              {t("enduser.plan.actions.proceed", { defaultValue: "Proceed to Payment" })}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
